import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, Phone } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ARTICLES, CATEGORIES } from "@/lib/articles";
import { SITE } from "@/lib/site";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
};

export const BlogList = () => {
  useEffect(() => {
    document.title = "Blog · Spectacle de drones — drones3x.fr";
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
      el.content = content;
    };
    setMeta("description", "Le blog drones3x.fr — guides, tarifs, idées et conseils sur le spectacle de drones lumineux en France et en Europe. Mariages, corporate, festivals, sport.");
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = "https://drones3x.fr/blog";
  }, []);

  const [featured, ...rest] = ARTICLES;

  return (
    <div className="App">
      <Navbar />
      <main className="pt-32 pb-28">
        <section className="relative">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(0,122,255,0.12),transparent_60%)] pointer-events-none" />
          <div className="relative mx-auto max-w-7xl px-6">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full glass px-4 py-2 text-xs uppercase tracking-[0.2em] text-white/70">
                Blog drones3x
              </div>
              <h1 className="font-display mt-6 text-4xl md:text-5xl lg:text-6xl font-light tracking-tight">
                Guides & inspirations sur le <span className="italic font-semibold text-[#007AFF]">spectacle de drones</span>.
              </h1>
              <p className="mt-6 text-white/65 leading-relaxed max-w-2xl">
                Tarifs, idées créatives, comparatifs, réglementation — tout ce que vous devez
                savoir avant d'organiser votre spectacle de drones en France, en Europe ou au Maroc.
              </p>
            </div>

            {/* Featured article */}
            <Link
              to={`/blog/${featured.slug}`}
              data-testid={`blog-featured-${featured.slug}`}
              className="group block mt-16 rounded-3xl overflow-hidden glass-strong"
            >
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative aspect-[4/3] md:aspect-auto overflow-hidden">
                  <img src={featured.cover} alt={`${featured.title} — spectacle de drone drones3x`} className="absolute inset-0 h-full w-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#030305] via-transparent to-transparent" />
                  <span
                    className="absolute top-5 left-5 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold"
                    style={{ background: CATEGORIES[featured.category].color, color: "#020205" }}
                  >
                    {CATEGORIES[featured.category].label}
                  </span>
                </div>
                <div className="p-8 md:p-10 flex flex-col justify-center">
                  <div className="flex items-center gap-4 text-xs text-white/50 mb-4">
                    <span>{formatDate(featured.publishedAt)}</span>
                    <span className="inline-flex items-center gap-1"><Clock size={12} /> {featured.readingMinutes} min</span>
                  </div>
                  <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight group-hover:text-[#007AFF] transition">
                    {featured.title}
                  </h2>
                  <p className="mt-4 text-white/65 leading-relaxed">
                    {featured.excerpt}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#007AFF]">
                    Lire le guide complet <ArrowRight size={16} className="group-hover:translate-x-1 transition" />
                  </span>
                </div>
              </div>
            </Link>

            {/* Grid of articles */}
            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {rest.map((a, idx) => (
                <motion.div
                  key={a.slug}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-10%" }}
                  transition={{ duration: 0.6, delay: idx * 0.08 }}
                >
                  <Link
                    to={`/blog/${a.slug}`}
                    data-testid={`blog-card-${a.slug}`}
                    className="group block h-full rounded-3xl overflow-hidden glass-strong"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img src={a.cover} alt={`${a.title} — spectacle de drone drones3x`} className="absolute inset-0 h-full w-full object-cover opacity-60 group-hover:opacity-90 group-hover:scale-105 transition duration-700" />
                      <span
                        className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold"
                        style={{ background: CATEGORIES[a.category].color, color: "#020205" }}
                      >
                        {CATEGORIES[a.category].label}
                      </span>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 text-xs text-white/50 mb-3">
                        <span>{formatDate(a.publishedAt)}</span>
                        <span className="inline-flex items-center gap-1"><Clock size={11} /> {a.readingMinutes} min</span>
                      </div>
                      <h3 className="font-display text-xl font-semibold tracking-tight group-hover:text-[#007AFF] transition leading-snug">
                        {a.title}
                      </h3>
                      <p className="mt-3 text-sm text-white/60 leading-relaxed line-clamp-3">
                        {a.excerpt}
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-20 rounded-3xl glass-strong p-10 md:p-14 text-center">
              <h3 className="font-display text-3xl md:text-4xl font-light">
                Prêt à offrir votre propre spectacle de drones ?
              </h3>
              <p className="mt-4 text-white/65 max-w-xl mx-auto">
                Devis sous 24h, transparence totale, équipe certifiée DGAC.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-4">
                <Link to="/#contact" className="inline-flex items-center gap-2 rounded-full bg-[#007AFF] px-7 py-3.5 text-sm font-semibold hover:shadow-[0_0_32px_rgba(0,122,255,0.55)] transition">
                  Demander un devis →
                </Link>
                <a href={SITE.phoneLink} className="inline-flex items-center gap-2 rounded-full glass px-7 py-3.5 text-sm font-medium hover:bg-white/10 transition">
                  <Phone size={14} /> {SITE.phoneDisplay}
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BlogList;
